/**
 * Monitoring CDN (beta) — the customer's CDN/availability monitors. Each monitor
 * probes an origin through a specific CDN platform; a created monitor feeds the
 * Dynamic Data Feed for that platform. Account-wide (service-scoped), so there's
 * no domain selector. List + create/edit/delete; the "Track" toggle is read-only
 * (the backend update body has no enable field).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, RotateCcw, Search, Pencil, Trash2, Plus } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, Pagination, type ColumnDef } from '@vnetwork/ui/data';
import {
  Badge,
  Button,
  Card,
  CardBody,
  IconButton,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  Switch,
  Tooltip,
  TooltipProvider,
} from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useWafMonitors,
  useDeleteWafMonitor,
  wafExtractError,
  type WafMonitor,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './WafMonitoringCdn.module.css';

const muted = (v: React.ReactNode) => <span style={{ color: 'var(--vn-fg-muted)' }}>{v}</span>;

const PAGE_SIZES = [10, 20, 50];

/** Humanize a monitor's probe type. */
const cdnType = (t: string) => (t === 'custom' ? 'Custom' : t === 'managed' ? 'Managed' : t || '—');

/** Badge tone for a monitor's provisioning state. */
function statusTone(status: string): 'success' | 'danger' | 'warning' | 'neutral' {
  const s = status.toLowerCase();
  if (s === 'completed' || s === 'active' || s === 'success') return 'success';
  if (s === 'failed' || s === 'error') return 'danger';
  if (s.includes('progress') || s.includes('deploy') || s === 'pending') return 'warning';
  return 'neutral';
}

/** Edit + delete actions for a monitor. */
function ActionsCell({
  serviceId,
  monitor,
  onEdit,
}: {
  serviceId: string;
  monitor: WafMonitor;
  onEdit: (m: WafMonitor) => void;
}) {
  const { show } = useToast();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const del = useDeleteWafMonitor(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Monitoring CDN deleted' }); trackFeatureAction('waf', 'monitor', 'delete'); setConfirmDelete(false); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Delete failed') }),
  });
  return (
    <div style={{ display: 'inline-flex', gap: 2 }}>
      <TooltipProvider>
        <Tooltip content="Edit" side="top">
          <IconButton variant="ghost" size="sm" aria-label="Edit monitor" onClick={() => onEdit(monitor)}>
            <Pencil size={14} />
          </IconButton>
        </Tooltip>
        <Tooltip content="Delete" side="top">
          <IconButton variant="ghost" size="sm" aria-label="Delete monitor" onClick={() => setConfirmDelete(true)}>
            <Trash2 size={14} />
          </IconButton>
        </Tooltip>
      </TooltipProvider>
      {confirmDelete && (
        <ConfirmDialog
          open
          onOpenChange={(o) => { if (!o) setConfirmDelete(false); }}
          title="Delete Monitoring CDN?"
          description={`Delete "${monitor.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          destructive
          loading={del.isPending}
          onConfirm={() => del.mutate({ id: monitor.id })}
        />
      )}
    </div>
  );
}

function MonitoringCdnBody({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');

  const goAdd = () => navigate(`/services/waf/${serviceId}/monitors/new`);
  const goEdit = (m: WafMonitor) => navigate(`/services/waf/${serviceId}/monitors/edit?id=${m.id}`);

  const monitorsQ = useWafMonitors(serviceId, { page, page_size: pageSize });

  const all = monitorsQ.data?.items ?? [];
  // Client-side search over the current page (name / platform).
  const items = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (m) =>
        (m.display_name || m.name).toLowerCase().includes(q) ||
        m.platform_name.toLowerCase().includes(q),
    );
  }, [all, search]);

  const pagination = monitorsQ.data?.pagination;
  const totalPages = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? all.length;
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = total === 0 ? 0 : start + all.length - 1;

  const columns = useMemo<ColumnDef<WafMonitor, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Monitoring CDN Name',
        enableSorting: false,
        cell: ({ row }) => (
          <span style={{ fontWeight: 500 }}>{row.original.display_name || row.original.name}</span>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        enableSorting: false,
        cell: ({ row }) => cdnType(row.original.type),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.status ? (
            <Badge tone={statusTone(row.original.status)} appearance="subtle" size="sm">
              {row.original.status}
            </Badge>
          ) : (
            muted('—')
          ),
      },
      {
        id: 'provider',
        header: 'CDN provider',
        enableSorting: false,
        cell: ({ row }) => row.original.platform_name || muted('—'),
      },
      {
        id: 'track',
        header: 'Track',
        enableSorting: false,
        // Display-only: the backend update body has no enable field.
        cell: ({ row }) => (
          <div style={{ display: 'inline-flex' }}>
            <Switch checked={row.original.enable} disabled aria-label="Track" />
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => <ActionsCell serviceId={serviceId} monitor={row.original} onEdit={goEdit} />,
      },
    ],
    [serviceId, goEdit],
  );

  return (
    <Card>
      <CardBody>
        <div className={styles.toolbar}>
          <div className={styles.search}>
            <Input
              placeholder="Search Monitoring CDN"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              startSlot={<Search size={15} />}
              aria-label="Search Monitoring CDN"
            />
          </div>
          <div className={styles.toolbarActions}>
            <Button
              variant="secondary"
              startIcon={<RotateCcw size={15} />}
              onClick={() => monitorsQ.refetch()}
              disabled={monitorsQ.isFetching}
              aria-label="Refresh monitors"
            >
              Refresh
            </Button>
            <Button startIcon={<Plus size={15} />} onClick={goAdd}>
              Add Monitoring CDN
            </Button>
          </div>
        </div>

        {monitorsQ.isPending ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 44 }} />
            ))}
          </div>
        ) : monitorsQ.isError ? (
          <Alert
            tone="danger"
            title="Couldn't load Monitoring CDN"
            actions={<button onClick={() => monitorsQ.refetch()}>Retry</button>}
          >
            {wafExtractError(monitorsQ.error, 'The WAF service couldn’t return your monitors. Try again.')}
          </Alert>
        ) : (
          <>
            <DataTable
              columns={columns}
              data={items}
              getRowId={(r) => String(r.id)}
              emptyState={
                <EmptyState
                  icon={<Activity size={20} />}
                  title={search ? 'No matching monitors' : 'No Monitoring CDN yet'}
                  description={
                    search
                      ? 'No monitors match your search on this page.'
                      : 'Create a Monitoring CDN to track your origin’s performance through a specific CDN.'
                  }
                  actions={
                    search ? undefined : (
                      <Button startIcon={<Plus size={15} />} onClick={goAdd}>
                        Add Monitoring CDN
                      </Button>
                    )
                  }
                />
              }
            />
            <div className={styles.footer}>
              <div className={styles.showRow}>
                <span>Show:</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}
                >
                  <SelectTrigger aria-label="Page size" style={{ width: 80 }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Pagination
                page={page}
                pageCount={totalPages}
                onPageChange={setPage}
                summary={monitorsQ.isFetching ? 'Loading…' : `${start}-${end} of ${total}`}
              />
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

export function WafMonitoringCdn({ serviceId }: { serviceId: string }) {
  return (
    <Container size="xl">
      <PageHeader
        icon={<Activity size={22} />}
        title="Monitoring CDN (beta version)"
        description="Create your own Monitoring CDN to monitor the performance of your origin through a specific CDN. The created monitor will be available on the Dynamic Data Feed for that CDN."
      />
      <MonitoringCdnBody serviceId={serviceId} />
    </Container>
  );
}

export default WafMonitoringCdn;
